import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Trophy, Home, Gamepad2, Bell, MessageSquare, 
  User, Menu, X, Search, Plus, Swords, Award, Shield
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

const ADMIN_EMAIL = "cpt.serdarakdemir@gmail.com";

export const Navbar = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (user) {
      api.getUnreadCount().then(res => {
        setUnreadCount(res.data.count);
      }).catch(() => {});
    }
  }, [user, location.pathname]);

  const navItems = [
    { path: "/dashboard", label: "Ana Sayfa", icon: Home },
    { path: "/games", label: "Oyunlar", icon: Gamepad2 },
    { path: "/tournaments", label: "Turnuvalar", icon: Swords },
    { path: "/badges", label: "Rozetler", icon: Award },
    { path: "/search", label: "Ara", icon: Search },
  ];
  
  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="sticky top-0 z-50 glass-card border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_game-tracker-92/artifacts/62gbiyum_WhatsApp%20Image%202026-01-30%20at%2002.16.12.jpeg" 
              alt="Kahve LİGİ Logo"
              className="w-16 h-16 rounded-xl object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="text-2xl font-bold tracking-wide gold-gradient-text font-['Barlow_Condensed'] uppercase">
                Kahve LİGİ
              </h1>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    isActive(item.path)
                      ? "bg-[#4DFFFF]/10 text-[#4DFFFF] border border-[#4DFFFF]/30"
                      : "text-[#B1C1C1] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-['Barlow_Condensed'] uppercase tracking-wider">
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                {/* Create League */}
                <Link to="/create-league" className="hidden sm:block">
                  <Button size="sm" className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
                    <Plus className="w-4 h-4 mr-1" />
                    Lig
                  </Button>
                </Link>

                {/* Admin Panel Button - Only for admin */}
                {isAdmin && (
                  <Link to="/admin" className="hidden sm:block" data-testid="admin-link">
                    <Button size="sm" variant="outline" className="border-[#C6A87C] text-[#C6A87C] hover:bg-[#C6A87C]/10">
                      <Shield className="w-4 h-4 mr-1" />
                      Admin
                    </Button>
                  </Link>
                )}

                {/* Notifications */}
                <Link to="/notifications" className="relative p-2 hover:bg-white/5 rounded-lg">
                  <Bell className="w-5 h-5 text-[#B1C1C1]" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-4 h-4 bg-[#F61225] rounded-full text-xs text-white flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>

                {/* Messages */}
                <Link to="/messages" className="p-2 hover:bg-white/5 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-[#B1C1C1]" />
                </Link>

                {/* Profile */}
                <Link 
                  to={`/profile/${user.username}`} 
                  className="flex items-center gap-2 p-2 hover:bg-white/5 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full bg-[#4DFFFF]/20 flex items-center justify-center">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-[#4DFFFF]">
                        {user.name?.charAt(0)}
                      </span>
                    )}
                  </div>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:bg-white/10">
                    Giriş
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#4DFFFF] text-[#050E2E] hover:bg-[#4DFFFF]/90">
                    Kayıt
                  </Button>
                </Link>
              </>
            )}

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-lg hover:bg-white/5"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#4DFFFF]" />
              ) : (
                <Menu className="w-6 h-6 text-[#B1C1C1]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                    isActive(item.path)
                      ? "bg-[#4DFFFF]/10 text-[#4DFFFF]"
                      : "text-[#B1C1C1] hover:text-white hover:bg-white/5"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-['Barlow_Condensed'] uppercase tracking-wider">
                    {item.label}
                  </span>
                </Link>
              );
            })}
            {user && (
              <>
                <Link
                  to="/create-league"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-[#4DFFFF] hover:bg-[#4DFFFF]/10"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-['Barlow_Condensed'] uppercase tracking-wider">
                    Yeni Lig
                  </span>
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-[#C6A87C] hover:bg-[#C6A87C]/10"
                  >
                    <Shield className="w-5 h-5" />
                    <span className="font-['Barlow_Condensed'] uppercase tracking-wider">
                      Admin Panel
                    </span>
                  </Link>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
